import { FC } from "react";

import { Box, Flex, HStack } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";

import { Button } from "@components/buttons";
import Link from "@components/Link";
import { Desktop } from "@lib/media";
import { useRouter } from "@lib/router";
import responsiveStyle from "@lib/theme/responsiveStyle";
import { useAuth } from "@modules/auth";
import { ROUTES } from "@utils/constants";

import { DeliveryMethod, OrderFields, OrderStep } from "../../constants";
import useSaveForLater from "../../hooks/summary/useSaveForLater";
import useOrderFormik from "../../hooks/useOrderFormik";

type TOrderFooterProps = {
  currentStep: OrderStep;
};

const OrderFooter: FC<TOrderFooterProps> = ({ currentStep }) => {
  const { t } = useTranslation();

  const {
    values: { [OrderFields.DELIVERY_METHOD]: selectedDeliveryMethod, [OrderFields.PRICE]: price },
  } = useOrderFormik();

  const router = useRouter();
  const { isLoggedIn } = useAuth();

  const { saveForLater } = useSaveForLater();

  const handleCancelClick = () => {
    if (currentStep === OrderStep.PackageInfo) {
      router.push(ROUTES.LANDING);
      return;
    }
    if (currentStep === OrderStep.ShipmentDetails || currentStep === OrderStep.SenderDetails) {
      router.push(ROUTES.ORDER);
      return;
    }
    if (currentStep === OrderStep.ReceiverDetails) {
      router.push(ROUTES.ORDER_SENDER);
      return;
    }
    if (currentStep === OrderStep.Summary) {
      const route =
        selectedDeliveryMethod === DeliveryMethod.PudoToPudo || selectedDeliveryMethod === DeliveryMethod.PudoToDoor
          ? ROUTES.ORDER_SHIPMENT
          : ROUTES.ORDER_RECEIVER;
      router.push(route);
    }
  };

  const handleSaveForLaterClick = () => saveForLater();
  const orderPrice = price || 0;

  const backActionTitle = currentStep === OrderStep.PackageInfo ? t("order-action-cancel") : t("order-action-back");

  const nextActionTitle =
    currentStep === OrderStep.Summary && orderPrice > 0 ? t("order-action-go_to_pay") : t("order-action-next");

  const isSaveForLaterVisible = currentStep === OrderStep.Summary && isLoggedIn;

  // Additional wrapper Box is needed, because VStack, used in parent component, overrides margins
  return (
    <Box>
      <Flex
        direction="row-reverse"
        px={responsiveStyle({ mobile: "0.5rem", desktop: "1.5rem" })}
        py={responsiveStyle({ mobile: "1rem", desktop: "1.125rem" })}
        mx={responsiveStyle({ mobile: "-0.5rem", desktop: "0" })}
        justify="space-between"
        background="grey.light"
      >
        <HStack spacing="2rem">
          <Desktop>
            {isSaveForLaterVisible && (
              <Link
                id="order-save-for-later"
                onClick={handleSaveForLaterClick}
                label={t("order-action-save_for_later")}
                size="lg"
              />
            )}
          </Desktop>
          <Button id="order-submit" type="submit" variant="main" label={nextActionTitle} />
        </HStack>
        <Button id="order-back" type="button" variant="secondary" label={backActionTitle} onClick={handleCancelClick} />
      </Flex>
    </Box>
  );
};
export default OrderFooter;
